<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import PageState from '@/components/PageState.vue';
import { errorMessage, formatDate } from '@/utils/format';

/**
 * 系统使用情况看板（0821 Tony 要的）。
 *
 * 这张看板要回答的是**推行到哪一步了**，不是"系统忙不忙"：
 * 给了 124 个人账号，真进来过的只有 16 个——这个数字比任何操作量都重要，
 * 所以它放在最显眼的位置，并且分母写清楚是「可登录账号」。
 *
 * 两个数字口径必须分开写，否则会被读成一回事：
 * - **登录活跃**：谁进过系统（会话/最后登录时间）；
 * - **操作量**：谁在系统里改了东西（审计只记写，不记浏览）。
 * 一个天天进来看计划、从不改东西的人，前者有他、后者没有。
 */
const props = defineProps<{ active: boolean }>();

interface Usage {
  asOf: string;
  totals: {
    accounts: number; loginEnabled: number; everLoggedIn: number;
    activeWeek: number; activeMonth: number; workingMonth: number;
  };
  daily: Array<{ day: string; actions: number; people: number; logins: number }>;
  modules: Array<{ moduleKey: string; label: string; actions: number; people: number }>;
  departments: Array<{
    orgName: string; accounts: number; loginEnabled: number;
    everLoggedIn: number; activeMonth: number;
  }>;
  topUsers: Array<{ name: string; actions: number; lastActiveAt: string | null }>;
  neverLoggedIn: Array<{ id: number; name: string; username: string; orgName: string | null }>;
  dormant: Array<{ id: number; name: string; orgName: string | null; lastLoginAt: string }>;
}

const data = ref<Usage | null>(null);
const loading = ref(false);
const error = ref('');
const days = ref(30);
const listTab = ref<'never' | 'dormant'>('never');

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    data.value = await eqsApi.usageStats(days.value) as unknown as Usage;
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

/** 页签切过来才拉数据：用户管理页多数时候是来改人的，不该白跑一次统计 */
watch(() => props.active, (on) => { if (on && !data.value) void load(); });
watch(days, () => { if (props.active) void load(); });
onMounted(() => { if (props.active) void load(); });

const adoption = computed(() => {
  const totals = data.value?.totals;
  if (!totals?.loginEnabled) return 0;
  return Math.round((totals.everLoggedIn / totals.loginEnabled) * 100);
});

/**
 * 日期标签隔几根显示一个（0825）。
 *
 * 柱子多到一定程度，标签就会挤成一条黑边——那既读不出日期、又把图压矮。
 * 按总根数算步长：30 天时每 5 根标一个，7 天时根根都标。
 * 首尾两根一定标，否则看不出这张图从哪天到哪天。
 */
function showDayLabel(index: number): boolean {
  const total = data.value?.daily.length ?? 0;
  if (total <= 10) return true;
  const step = Math.ceil(total / 6);
  return index === 0 || index === total - 1 || index % step === 0;
}

const maxActions = computed(() =>
  Math.max(1, ...(data.value?.daily ?? []).map((item) => item.actions)));
const maxModule = computed(() =>
  Math.max(1, ...(data.value?.modules ?? []).map((item) => item.actions)));

/** 部门渗透率：这个部门有账号的人里，进来过的占多少 */
function penetration(row: Usage['departments'][number]): number {
  return row.loginEnabled ? Math.round((row.everLoggedIn / row.loginEnabled) * 100) : 0;
}

function toneOf(percent: number): string {
  if (percent >= 60) return 'ok';
  if (percent >= 25) return 'warn';
  return 'risk';
}
</script>

<template>
  <section v-if="props.active" class="usage">
    <PageState v-if="loading && !data" state="loading" title="正在统计使用情况" message="按账号、审计留痕汇总中。" />
    <div v-else-if="error" class="inline-alert"><b>读不到统计</b><span>{{ error }}</span></div>

    <template v-else-if="data">
      <header class="usage-head">
        <div>
          <h2>系统使用情况</h2>
          <p>
            截至 {{ formatDate(data.asOf) }}。
            <b>登录活跃</b>= 谁进过系统；<b>操作量</b>= 谁在系统里改了东西（留痕只记写操作，<b>不含浏览</b>）。
          </p>
        </div>
        <label class="usage-range">
          <span>统计区间</span>
          <select v-model.number="days">
            <option :value="7">近 7 天</option>
            <option :value="30">近 30 天</option>
            <option :value="90">近 90 天</option>
          </select>
        </label>
        <button class="button" type="button" :disabled="loading" @click="load">
          {{ loading ? '刷新中…' : '刷新' }}
        </button>
      </header>

      <!-- 推行进度先说：给了多少账号、真进来几个 -->
      <div class="usage-kpis">
        <article>
          <b>{{ data.totals.loginEnabled }}</b>
          <span>可登录账号</span>
          <small>组织里共 {{ data.totals.accounts }} 个账号</small>
        </article>
        <article :class="['is-' + toneOf(adoption)]">
          <b>{{ data.totals.everLoggedIn }}<em>{{ adoption }}%</em></b>
          <span>登录过的人</span>
          <small>还有 {{ data.totals.loginEnabled - data.totals.everLoggedIn }} 个人一次都没进来过</small>
        </article>
        <article>
          <b>{{ data.totals.activeWeek }}</b>
          <span>近 7 天登录</span>
          <small>近 30 天 {{ data.totals.activeMonth }} 人</small>
        </article>
        <article>
          <b>{{ data.totals.workingMonth }}</b>
          <span>近 30 天动过数据</span>
          <small>登录了但没改东西的不算在内</small>
        </article>
      </div>

      <div class="usage-grid">
        <section class="content-panel usage-card">
          <header><b>每天的操作量</b><small>柱=操作数，点=当天有动作的人数</small></header>
          <!--
            30 根柱子配 30 个日期标签必然糊成一片（0825 Tony 截图里就是一团黑）。
            隔几根显示一个：够定位到大概哪一天，鼠标悬上去有完整日期。
          -->
          <ul class="usage-trend">
            <li
              v-for="(item, index) in data.daily" :key="item.day"
              :title="`${item.day}：${item.actions} 次操作 · ${item.people} 人`"
            >
              <i :style="{ height: `${Math.round((item.actions / maxActions) * 100)}%` }" />
              <em v-if="item.people" :style="{ bottom: `${Math.round((item.actions / maxActions) * 100)}%` }" />
              <span>{{ showDayLabel(index) ? item.day : '' }}</span>
            </li>
          </ul>
        </section>

        <section class="content-panel usage-card">
          <header><b>哪些模块在被用</b><small>按写操作次数</small></header>
          <ul class="usage-bars">
            <li v-for="item in data.modules" :key="item.moduleKey">
              <span>{{ item.label }}</span>
              <i :style="{ width: `${Math.round((item.actions / maxModule) * 100)}%` }" />
              <b>{{ item.actions }}</b>
              <small>{{ item.people }} 人</small>
            </li>
          </ul>
          <p v-if="!data.modules.length" class="usage-empty">这个区间里没有任何写操作。</p>
        </section>

        <section class="content-panel usage-card usage-wide">
          <header><b>各部门用起来了吗</b><small>渗透率 = 进来过的人 ÷ 有账号能登录的人</small></header>
          <div class="usage-table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>部门</th><th>可登录</th><th>登录过</th><th>近 30 天</th><th class="usage-col-rate">渗透率</th></tr>
              </thead>
              <tbody>
                <tr v-for="row in data.departments" :key="row.orgName">
                  <td>{{ row.orgName }}</td>
                  <td>{{ row.loginEnabled }}</td>
                  <td>{{ row.everLoggedIn }}</td>
                  <td>{{ row.activeMonth }}</td>
                  <!--
                    渗透率：数字 + 一条**在固定宽度轨道里**的进度条（0825 修版式）。
                    原来进度条是 `position:absolute` + `width: N%`，百分比相对整个单元格算，
                    而单元格宽度随内容变——于是 29% 和 60% 画出来差不多长，
                    这个图不是不好看，是**在骗人**。
                    现在轨道宽度固定，条长才真正代表比例。
                  -->
                  <td class="usage-col-rate">
                    <b>{{ penetration(row) }}%</b>
                    <span class="usage-rate-track">
                      <i
                        :class="['usage-rate', 'is-' + toneOf(penetration(row))]"
                        :style="{ width: `${penetration(row)}%` }"
                      />
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section class="content-panel usage-card">
          <header><b>动手最多的人</b><small>这个区间里的写操作</small></header>
          <ol class="usage-top">
            <li v-for="(row, index) in data.topUsers" :key="row.name">
              <em>{{ index + 1 }}</em>
              <span>{{ row.name }}</span>
              <b>{{ row.actions }}</b>
              <small>{{ row.lastActiveAt ? formatDate(row.lastActiveAt) : '—' }}</small>
            </li>
          </ol>
          <p v-if="!data.topUsers.length" class="usage-empty">这个区间里没人动过数据。</p>
        </section>

        <!-- 推行抓手：这两张名单是拿去打电话的，不是看着玩的 -->
        <section class="content-panel usage-card">
          <header>
            <b>该找谁</b>
            <span class="usage-list-tabs">
              <button type="button" :class="{ on: listTab === 'never' }" @click="listTab = 'never'">
                从没登录（{{ data.neverLoggedIn.length }}）
              </button>
              <button type="button" :class="{ on: listTab === 'dormant' }" @click="listTab = 'dormant'">
                30 天没来（{{ data.dormant.length }}）
              </button>
            </span>
          </header>
          <ul v-if="listTab === 'never'" class="usage-people">
            <li v-for="row in data.neverLoggedIn.slice(0, 40)" :key="row.id">
              <b>{{ row.name }}</b><small>{{ row.orgName ?? '未挂部门' }} · {{ row.username }}</small>
            </li>
          </ul>
          <ul v-else class="usage-people">
            <li v-for="row in data.dormant.slice(0, 40)" :key="row.id">
              <b>{{ row.name }}</b>
              <small>{{ row.orgName ?? '未挂部门' }} · 最后登录 {{ formatDate(row.lastLoginAt) }}</small>
            </li>
          </ul>
          <p v-if="listTab === 'never' && data.neverLoggedIn.length > 40" class="usage-empty">
            只列了前 40 个，共 {{ data.neverLoggedIn.length }} 人。
          </p>
          <p v-if="listTab === 'dormant' && !data.dormant.length" class="usage-empty">
            没有超过 30 天没来的人。
          </p>
        </section>
      </div>
    </template>
  </section>
</template>

<style scoped>
.usage { display: grid; gap: 14px; }
.usage-head { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
.usage-head h2 { margin: 0 0 4px; font-size: 18px; }
.usage-head p { margin: 0; max-width: 720px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.usage-range { display: grid; gap: 4px; font-size: 12px; color: var(--muted); }

.usage-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; }
.usage-kpis article {
  display: grid; gap: 3px; padding: 14px 16px;
  border: 1px solid var(--line); border-radius: 12px; background: var(--white);
}
.usage-kpis b { display: flex; align-items: baseline; gap: 8px; font-size: 30px; line-height: 1.1; color: var(--ink); }
.usage-kpis b em { font-style: normal; font-size: 14px; font-weight: 700; color: var(--blue); }
.usage-kpis span { font-size: 13px; font-weight: 600; }
.usage-kpis small { color: var(--muted); font-size: 11px; line-height: 1.6; }
.usage-kpis article.is-risk { border-color: rgba(200, 60, 60, .45); background: rgba(200, 60, 60, .04); }
.usage-kpis article.is-risk b em { color: #c83c3c; }
.usage-kpis article.is-warn { border-color: rgba(210, 150, 30, .45); background: rgba(210, 150, 30, .05); }

/*
  两列布局，同一行的两块**等高**（0825 修版式）。

  原来是 `align-items: start`：左边「每天的操作量」矮、右边「哪些模块在被用」高，
  两块顶部对齐、底部参差，中间豁一大块白——一屏里最显眼的凌乱就是这个。
  改成默认拉伸等高（去掉 align-items），卡片内部用 flex 把内容顶上去。
*/
.usage-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 12px; }
.usage-card { display: flex; flex-direction: column; padding: 14px 16px; }
.usage-wide { grid-column: 1 / -1; }
/* 卡片内容区吃掉剩余高度，列表和图表都贴着顶排，不会被拉伸变形 */
.usage-card > :last-child { margin-bottom: 0; }
.usage-card > header { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.usage-card > header b { font-size: 14px; }
.usage-card > header small { color: var(--muted); font-size: 11px; }
.usage-empty { margin: 8px 0 0; color: var(--muted); font-size: 12px; }

/* 趋势：柱 = 操作数，点 = 当天有动作的人 */
.usage-trend { display: flex; align-items: flex-end; gap: 3px; height: 150px; margin: 0; padding: 0; list-style: none; }
.usage-trend li { position: relative; display: flex; flex: 1; flex-direction: column; justify-content: flex-end; align-items: center; height: 100%; min-width: 0; }
.usage-trend i { display: block; width: 100%; min-height: 2px; border-radius: 3px 3px 0 0; background: var(--blue); opacity: .82; }
.usage-trend em { position: absolute; width: 5px; height: 5px; margin-bottom: 2px; border-radius: 50%; background: #d2961e; }
.usage-trend span { margin-top: 4px; color: var(--muted); font-size: 9px; white-space: nowrap; overflow: hidden; }

.usage-bars { flex: 1; min-height: 0; display: grid; gap: 7px; align-content: start; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
/* 模块名那列用 minmax 不用死宽度：`PFMEA / 控制计划` 比 `看板` 长一倍 */
.usage-bars li { display: grid; grid-template-columns: minmax(86px, 128px) minmax(0, 1fr) 44px 44px; align-items: center; gap: 8px; font-size: 12px; }
.usage-bars li > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.usage-bars i { display: block; height: 10px; min-width: 3px; border-radius: 999px; background: var(--blue); opacity: .82; }
.usage-bars b { text-align: right; }
.usage-bars small { color: var(--muted); text-align: right; }

/*
  表格**只在真的很长时才滚**（0825）。原来死设 max-height:320px，
  于是 5 行也套一个纵向滚动条，再叠上横向滚动条——两条滚动条框着五行数据，
  是这一屏最杂乱的一块。`overflow-x:auto` 单独留着：窄屏时表格自己横滚是对的，
  但不该连带把整页撑出横向滚动。
*/
.usage-table-wrap { max-height: 460px; overflow-y: auto; overflow-x: auto; }
.usage-table-wrap table { min-width: 520px; }

/*
  渗透率：数字在左、固定宽度的轨道在右。
  轨道固定宽才有可比性——原来条长按单元格百分比算，
  单元格宽度随内容变，29% 和 60% 画出来一样长。
*/
.usage-col-rate { min-width: 150px; }
.usage-col-rate b { display: inline-block; min-width: 38px; font-variant-numeric: tabular-nums; }
.usage-rate-track { display: inline-block; width: 90px; height: 6px; margin-left: 8px; border-radius: 999px; background: var(--line); overflow: hidden; vertical-align: middle; }
.usage-rate { display: block; height: 100%; min-width: 2px; border-radius: 999px; background: var(--blue); }
.usage-rate.is-ok { background: #2e9e6b; }
.usage-rate.is-warn { background: #d2961e; }
.usage-rate.is-risk { background: #c83c3c; }

/* 同 usage-people：卡片有高度上限，这里就得能滚，否则内容被裁掉还看不出来 */
.usage-top { flex: 1; min-height: 0; display: grid; gap: 6px; align-content: start; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
.usage-top li { display: grid; grid-template-columns: 20px 1fr 48px 78px; align-items: center; gap: 8px; font-size: 12px; }
.usage-top em { font-style: normal; color: var(--muted); }
.usage-top b { text-align: right; }
.usage-top small { color: var(--muted); text-align: right; }

.usage-list-tabs { display: inline-flex; gap: 6px; }
.usage-list-tabs button {
  padding: 3px 10px; border: 1px solid var(--line); border-radius: 999px;
  background: transparent; color: var(--muted); font-size: 11px; cursor: pointer;
}
.usage-list-tabs button.on { border-color: var(--blue); background: rgba(18, 109, 166, .08); color: var(--blue); font-weight: 700; }
/*
  「该找谁」是一份 108 人的长名单，必须能滚。

  两个坑都踩过，所以两条都要写：
  - 只写 `flex:1` 不写 `min-height:0`：flex 子项默认 `min-height:auto`，
    会被内容撑到全高，overflow 永远不生效——实测 108 人时卡片撑到 2671px，
    旁边那块被迫跟着拉一样高，等高反而成了灾难。
  - 只写 `max-height` 不写 `flex`：死高度和左边「动手最多的人」永远对不齐，
    那正是 0825 之前的样子。
  两条一起：跟着卡片高度走，但**卡片自己有上限**（下面 max-height）。
*/
.usage-people { flex: 1; min-height: 0; max-height: 340px; display: grid; gap: 5px; align-content: start; overflow-y: auto; margin: 0; padding: 0; list-style: none; }
.usage-people li { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; font-size: 12px; }
.usage-people small { color: var(--muted); font-size: 11px; white-space: nowrap; }
/* 卡片给上下限：内容少时不瘪成一条，内容多时不拉成一根柱子把同行的也拽长 */
.usage-grid .usage-card { min-height: 200px; max-height: 460px; }
.usage-grid .usage-card.usage-wide { max-height: none; }
</style>
